import React, {useState} from 'react';
import ClientNavbar from "../../components/ClientNavbar";
import clsx from "clsx";
import {Button, Loader} from "../../components/common";
import {CreateRequestModal, GenerationLinkModal} from "../../components/modal";
import {useQuery} from "react-query";
import {ALERT_TYPES, LS_KEYS} from "../../types";
import {getFromLS} from "../../utils/localStorage";
import {useAlert} from "../../components/common/Alert";

interface IProducts {
  id: number;
  name: string;
  type: string;
  description?: string;
  logo: null;
  bank: string;
  reward: string;
}

interface IFilters {
  types: Type[];
}

interface Type {
  name: string;
  label: string;
}

const ClientProduct = ({ isLanding = false }: { isLanding?: boolean }) => {
  const { setAlert } = useAlert();
  const isAuth = getFromLS(LS_KEYS.ACCESS_TOKEN);

  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | number>('');
  const [isOpenRequestModal, setIsOpenRequestModal] = useState<boolean>(false);
  const [isOpenGenerationModal, setIsOpenGenerationModal] = useState<boolean>(false);

  const { data, isLoading, error }: {
    data: Array<IProducts> | undefined; isLoading: boolean; error: any;
  } = useQuery(["productsList", activeFilter], {
    enabled: !isLanding,
    refetchOnWindowFocus: false
  });

  const { data: filters, isLoading: loading, error: filterError }: {
    data: IFilters | undefined; isLoading: boolean; error: any;
  } = useQuery(["productsFilters"], {
    enabled: !isLanding,
    refetchOnWindowFocus: false
  });

  const { data: publicFilters, isLoading: publicFilterLoading, error: publicFilterError }: {
    data: IFilters | undefined; isLoading: boolean; error: any;
  } = useQuery(["publicProductsFilters"], {
    enabled: isLanding,
    refetchOnWindowFocus: false
  });

  const { data: publicProduct, isLoading: publicLoading, error: publicError }: {
    data: Array<IProducts> | undefined; isLoading: boolean; error: any;
  } = useQuery(["publicProductsList", activeFilter], {
    enabled: isLanding,
    refetchOnWindowFocus: false
  });

  const errorMessage = error || filterError || publicFilterError || publicError;

  if (errorMessage) {
    setAlert(`${errorMessage?.response.data.type?.[0]}`, ALERT_TYPES.ERROR);
  }

  const handleCloseModal = () => setIsOpenRequestModal(false);

  const handleCloseGenerationModal = () => setIsOpenGenerationModal(false);

  const handleOpenModal = (id: string | number) => () => {
    setSelectedId(id)

    if (activeFilter === 'cpa') {
      setIsOpenGenerationModal(true);
      return;
    }
    setIsOpenRequestModal(true);
  }

  const arrayOfProducts = isLanding ? publicProduct : data;
  const arrayOfFilters = isLanding ? publicFilters : filters

  return (
    <>
      {!isLanding && <ClientNavbar/>}
      {
        isAuth &&
        <CreateRequestModal
          open={isOpenRequestModal}
          onClose={handleCloseModal}
          isProduct={true}
          productID={selectedId}
        />
      }
      <GenerationLinkModal
        open={isOpenGenerationModal}
        onClose={handleCloseGenerationModal}
        productID={selectedId}
      />
      {(isLoading || publicLoading || loading || publicFilterLoading) && <Loader />}
      <div className="py-8 mx-auto max-w-[1320px] w-full px-4">
        <div className={
          clsx(
            "flex items-center justify-between lg:flex-col flex-wrap",
            isLanding && "flex-col"
          )
        }>
          <p className="text-mainBlack font-bold text-[32px] leading-[39px] lg:mb-3">
            { isLanding ? 'Партнерские продукты' : 'Продукты' }
          </p>
          <ul className="flex items-center gap-1 mt-[14px] justify-center lg:flex-wrap">
            <li
              className={
                clsx(
                  "text-black font-medium text-[14px] leading-[16px  rounded-sm py-[6px] px-[16px] cursor-pointer hover:bg-yellow duration-150 ease-in",
                  !activeFilter ? "bg-yellow" : "bg-mainBlack/5"
                )}
              onClick={() => setActiveFilter(null)}
            >
              Все
            </li>
            {arrayOfFilters?.types?.map((item) => (
              <li
                key={item.label}
                className={
                  clsx(
                    "text-black font-medium text-[14px] leading-[16px  rounded-sm py-[6px] px-[16px] cursor-pointer hover:bg-yellow duration-150 ease-in",
                    item?.name === activeFilter ? "bg-yellow" : "bg-mainBlack/5"
                  )}
                onClick={() => setActiveFilter(item?.name)}
              >{item.label}</li>
            ))}
          </ul>
        </div>

        <div className="relative mt-7 grid grid-cols-3 gap-[10px] z-1 lg:grid-cols-1">
          {!!arrayOfProducts?.length
            ? arrayOfProducts?.map((item, index) => (
              <div
                key={index}
                className="py-[32px] px-[25px] w-full relative h-[234px] bg-cardBg group"
              >
                <div className="relative z-10 h-full text-white flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white text-[20px] leading-[22px] mb-[6px]">{item.name}</p>
                    <p className="font-medium text-white text-[20px] leading-[22px] mb-[6px]">{item.bank}</p>
                    <span
                      className="text-yellow font-medium text-[20px] leading-[22px] group-hover:text-white"
                    >{item.reward}</span>
                    {isAuth &&
                      <Button
                        title={activeFilter === 'cpa' ? 'Сгенерировать ссылку' : 'Создать заявку'}
                        className="mt-8 whitespace-nowrap hover:scale-110 duration-150 ease-in"
                        onClick={handleOpenModal(item.id)}
                      />
                    }

                    <p className="mt-4 text-sm text-white">
                      {item.description}
                    </p>
                  </div>

                  {item.logo &&
                    <img
                      className="max-w-[170px] max-h-[170px] object-cover sm:max-w-[120px] sm:max-h-[120px]"
                      src={`${process.env.REACT_APP_BASE_URL}${item.logo}`}
                      alt="img"
                    />
                  }
                </div>
              </div>
            ))
            : <p className="text-center">Нет доступных продуктов</p>
          }
        </div>
      </div>
    </>
  )
};

export default ClientProduct;
